/**
 * JobCard - OCR job card with status, progress bar, record counts
 * Scientific dashboard card with instrument-style progress indicators
 */

import { cn } from '@/lib/cn';
import {
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  FileImage,
  FileCheck,
  AlertTriangle,
  ChevronRight,
  Trash2,
} from 'lucide-react';

type JobStatus = 'pending' | 'processing' | 'complete' | 'failed' | 'cancelled';

interface JobProgress {
  total_images: number;
  processed_images: number;
  percent_complete: number;
}

interface JobResults {
  total_records: number;
  needs_review: number;
  ready_to_import: number;
}

interface Job {
  id: string;
  farm_id: string;
  status: JobStatus;
  progress: JobProgress;
  results?: JobResults;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

interface JobCardProps {
  job: Job;
  onClick?: () => void;
  onDelete?: () => void;
  className?: string;
}

const statusConfig: Record<JobStatus, { icon: typeof Clock; color: string; label: string }> = {
  pending: {
    icon: Clock,
    color: 'text-gray-500 bg-gray-100',
    label: 'Pending',
  },
  processing: {
    icon: Loader2,
    color: 'text-blue-600 bg-blue-50',
    label: 'Processing',
  },
  complete: {
    icon: CheckCircle2,
    color: 'text-emerald-600 bg-emerald-50',
    label: 'Complete',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-600 bg-red-50',
    label: 'Failed',
  },
  cancelled: {
    icon: XCircle,
    color: 'text-gray-500 bg-gray-100',
    label: 'Cancelled',
  },
};

export function JobCard({ job, onClick, onDelete, className }: JobCardProps) {
  const config = statusConfig[job.status];
  const StatusIcon = config.icon;
  const isClickable = onClick && (job.status === 'complete' || job.status === 'processing');

  return (
    <div
      className={cn(
        'group relative bg-white rounded-xl border border-gray-200 overflow-hidden',
        'transition-all duration-200',
        isClickable && 'cursor-pointer hover:shadow-md hover:border-gray-300',
        className
      )}
      onClick={isClickable ? onClick : undefined}
    >
      {/* Status strip on left */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1',
          job.status === 'pending' && 'bg-gray-300',
          job.status === 'processing' && 'bg-blue-500',
          job.status === 'complete' && 'bg-emerald-500',
          job.status === 'failed' && 'bg-red-500',
          job.status === 'cancelled' && 'bg-gray-300'
        )}
      />

      <div className="pl-4 pr-3 py-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Status badge */}
            <div className={cn('p-1.5 rounded-lg shrink-0', config.color)}>
              <StatusIcon
                className={cn(
                  'w-4 h-4',
                  job.status === 'processing' && 'animate-spin'
                )}
              />
            </div>

            {/* Job info */}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-400">
                  #{job.id.slice(0, 8)}
                </span>
                <span className={cn(
                  'text-xs font-medium px-1.5 py-0.5 rounded',
                  config.color
                )}>
                  {config.label}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-0.5">
                {formatDate(job.created_at)}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {onDelete && job.status !== 'processing' && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                title="Delete job"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {isClickable && (
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
            )}
          </div>
        </div>

        {/* Progress section (during processing) */}
        {job.status === 'processing' && (
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500 flex items-center gap-1.5">
                <FileImage className="w-3.5 h-3.5" />
                Processing images
              </span>
              <span className="font-mono text-gray-700">
                {job.progress.processed_images}/{job.progress.total_images}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${job.progress.percent_complete}%` }}
              />
            </div>
          </div>
        )}

        {/* Results section (when complete) */}
        {job.status === 'complete' && job.results && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <ResultStat
              icon={FileCheck}
              label="Total"
              value={job.results.total_records}
              color="text-gray-600"
            />
            <ResultStat
              icon={CheckCircle2}
              label="Ready"
              value={job.results.ready_to_import}
              color="text-emerald-600"
            />
            <ResultStat
              icon={AlertTriangle}
              label="Review"
              value={job.results.needs_review}
              color={job.results.needs_review > 0 ? 'text-amber-600' : 'text-gray-400'}
            />
          </div>
        )}

        {/* Error message (when failed) */}
        {job.status === 'failed' && job.error_message && (
          <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-100">
            <p className="text-xs text-red-700 line-clamp-2">{job.error_message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ResultStat - Compact result statistic
 */
interface ResultStatProps {
  icon: typeof FileCheck;
  label: string;
  value: number;
  color: string;
}

function ResultStat({ icon: Icon, label, value, color }: ResultStatProps) {
  return (
    <div className="flex flex-col items-center p-2 bg-gray-50 rounded-lg">
      <Icon className={cn('w-4 h-4 mb-1', color)} />
      <span className="text-lg font-bold font-mono text-gray-900">{value}</span>
      <span className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
  );
}

/**
 * JobCardSkeleton - Loading state
 */
export function JobCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-gray-200 rounded-lg" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/4" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
        </div>
      </div>
    </div>
  );
}

// Helper
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}
